import React, { useCallback } from 'react';
import cogoToast from 'cogo-toast';
import { components } from 'react-select';
import { DateUtils } from '@vectord/utils';
import PropTypes from 'prop-types';

import { ACADEMY_TOPIC_TYPES } from 'data/academyTopicTypes';
import { DEBOUNCE_DURATION } from 'data/asyncSelect';
import { useLazySearchAcademyTopicQuery } from 'services/academyTopicService';
import Select from 'components/Select';
import useDebouncedPromise from 'hooks/useDebouncedPromise';

const MIN_KEYWORD_LENGTH = 3;

// Why not create a separate component file for this? Because we don't have to
// export this and the current file is well under limit.
function AcademyTopicOption(props) {
  return (
    <components.Option {...props}>
      <div className="h4">{props.data.label}</div>
      <div className="h6 no-mgn-b">
        Updated on {DateUtils.toDDMMMYYYY(props.data.updatedAt)} by{' '}
        {props.data.updatedBy?.email || 'N/A'}
      </div>
      <div className="h6">
        Created on {DateUtils.toDDMMMYYYY(props.data.createdAt)}
      </div>
    </components.Option>
  );
}

function AcademyTopicSelect({
  type,
  excludedAcademyTopicIds = [],
  ...remainingProps
}) {
  const [fetchTopics, { isFetching }] = useLazySearchAcademyTopicQuery();

  const generateOptions = useCallback(
    (academyTopics) => {
      return academyTopics
        .filter((o) => !excludedAcademyTopicIds.includes(o.id))
        .map((o) => ({
          value: o.id,
          label: o.title,
          createdAt: o.created_at,
          updatedAt: o.updated_at,
          updatedBy: o.updated_by_user,
        }));
    },
    [excludedAcademyTopicIds]
  );

  const loadOptions = useDebouncedPromise(
    async (keyword, callback) => {
      if (keyword.length < MIN_KEYWORD_LENGTH) return callback([]);

      const response = await fetchTopics({ topicType: type, keyword }, true);
      let options = [];
      if (response.data) {
        options = generateOptions(response.data.data);
      } else {
        cogoToast.error(
          response.error?.data?.message ||
            'Failed to load classes! Try again after sometime'
        );
      }
      callback(options);
    },
    DEBOUNCE_DURATION,
    [fetchTopics, generateOptions, type]
  );

  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (inputValue.length < MIN_KEYWORD_LENGTH) {
        return `Type atleast ${MIN_KEYWORD_LENGTH} characters to search`;
      } else if (isFetching) {
        return `Searching...`;
      } else {
        return `No matching class templates found.`;
      }
    },
    [isFetching]
  );

  return (
    <Select
      components={{ Option: AcademyTopicOption }}
      type="async"
      placeholder="Select a class"
      loadOptions={loadOptions}
      noOptionsMessage={noOptionsMessage}
      {...remainingProps}
    />
  );
}

AcademyTopicSelect.propTypes = {
  type: PropTypes.oneOf(Object.values(ACADEMY_TOPIC_TYPES)).isRequired,
  excludedAcademyTopicIds: PropTypes.arrayOf(PropTypes.number),
};

export default AcademyTopicSelect;
